import * as z from 'zod/v4';

import { BadDataError } from '@watershed/errors/BadDataError';

import { symmetricDifference } from '../../utils/setUtils';

// Customization parameter schema
const customizationParameterSchema = z.object({
  name: z.string(),
  expression: z.string(),
  mode: z.enum(['replace', 'coalesce']),
});

// Customization content input schema (reference data versions are derived)
const customizationContentInputSchema = z.object({
  parameters: z.array(customizationParameterSchema),
});

// Customization content schema for stored instances
const customizationContentSchema = customizationContentInputSchema.extend({
  referenceDataVersions: z.array(z.string()),
});

export namespace contentUtils {
  /**
   * Represents a single customization parameter.
   */
  export type CustomizationParameter = z.infer<
    typeof customizationParameterSchema
  >;
  /**
   * Represents the input content of a calculation customization.
   */
  export type CustomizationContentInput = z.infer<
    typeof customizationContentInputSchema
  > & {
    referenceDataVersions?: Array<string>;
  };
  /**
   * Represents the structured content of a calculation customization.
   */
  export type CustomizationContent = z.infer<typeof customizationContentSchema>;
  /**
   * Utility type that adds a parsed `CustomizationContent` field to an existing
   * type.
   */
  export type WithParsedContent<T, K extends keyof T> = T & {
    [k in K]: CustomizationContent;
  };

  /**
   * Unfortunately the inferred Zod type breaks our temporal codegen. This type
   * is a workaround to allow us to use the CustomizationContent type
   * in temporal workflows.
   */
  export type TemporalCustomizationContent = {
    parameters: Array<{
      name: string;
      expression: string;
      mode: 'replace' | 'coalesce';
    }>;
  };

  /**
   * Parses unknown input into a validated `CustomizationContent` object.
   */
  export function parseContent(
    content: unknown
  ): z.ZodSafeParseResult<CustomizationContent> {
    return customizationContentSchema.safeParse(content);
  }

  /**
   * Parses unknown input into a validated `CustomizationContentInput` object.
   */
  export function parseContentInput(
    content: unknown
  ): z.ZodSafeParseResult<CustomizationContentInput> {
    return customizationContentInputSchema.safeParse(content);
  }

  /**
   * Takes an object and a key, parses the value at that key as
   * `CustomizationContent`, and returns a new object with the parsed content
   * attached. Throws `BadDataError` if parsing fails.
   */
  export function withParsedContent<
    K extends keyof T,
    T extends { [key in K]: object },
  >(obj: T, key: K): WithParsedContent<T, K> {
    const parseResult = parseContent(obj[key]);
    if (!parseResult.success) {
      throw new BadDataError('Invalid customization content', {
        cause: parseResult.error,
        data: { parseResult, input: obj },
      });
    }
    return {
      ...obj,
      [key]: parseResult.data,
    };
  }

  /**
   * Returns true if the two content objects are not semantically equivalent.
   */
  export function contentDiffers(
    a: CustomizationContent,
    b: CustomizationContent
  ): boolean {
    return (
      parametersDiffer(a, b) ||
      referenceDataVersionsArraysAreDifferent(
        a.referenceDataVersions,
        b.referenceDataVersions
      )
    );
  }

  /**
   * Returns true if the parameters differ between two content objects.
   */
  export function parametersDiffer(
    a: { parameters: Array<CustomizationParameter> },
    b: { parameters: Array<CustomizationParameter> }
  ): boolean {
    return parameterArraysAreDifferent(a.parameters, b.parameters);
  }
}

function referenceDataVersionsArraysAreDifferent(
  a: Array<string>,
  b: Array<string>
) {
  if (a.length !== b.length) {
    return true;
  }

  return symmetricDifference(new Set(a), new Set(b)).size > 0;
}

function parameterArraysAreDifferent(
  aParams: Array<contentUtils.CustomizationParameter>,
  bParams: Array<contentUtils.CustomizationParameter>
) {
  if (aParams.length !== bParams.length) {
    return true;
  }

  for (const aParam of aParams) {
    const bParam = bParams.find((p) => p.name === aParam.name);
    if (
      !bParam ||
      aParam.expression !== bParam.expression ||
      aParam.mode !== bParam.mode
    ) {
      return true;
    }
  }

  return false;
}
